import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

/**
 * Renders animation_data elements at their canvas positions for any slide type.
 *
 * For question slides (poll / multiple / text), pass the interactive props to
 * enable attendee interaction. Omit them for read-only views (Speaker preview).
 *
 * Interactive props (all optional — omit for read-only):
 *   selectedPollOption, selectedMultipleOptions, onChoiceClick(choiceId, isMultiple),
 *   submittedAnswers, currentSlide, answer, onAnswerChange
 */
export const SlideCanvasRenderer = ({
  slide,
  // unified answer state — pass to enable interactive mode (Attendee)
  answerState,
  onAnswerChange,
  submittedAnswers,
  currentSlide,
}) => {
  if (!slide || !slide.type) return null;

  const elements = slide?.animation_data?.elements || [];
  const canvasBackground = slide?.animation_data?.canvasBackground || null;
  const canvasBackgroundColor =
    slide?.animation_data?.canvasBackgroundColor || "#f3f4f6";
  const isQuestion =
    slide.type === "poll" || slide.type === "multiple" || slide.type === "text";
  const isInteractive = !!onAnswerChange;

  // ── Question slides ────────────────────────────────────────────────────────
  if (isQuestion && elements.length > 0) {
    const questionEl = elements.find(
      (e) => e.elementRole === "question" || e.name === "Question",
    );
    const isChoiceEl = (e) =>
      e.elementRole === "choice" || /^Choice \d+$/.test(e.name);
    const isQuestionEl = (e) =>
      e.elementRole === "question" || e.name === "Question";

    const choiceElMap = Object.fromEntries(
      elements.filter(isChoiceEl).map((e) => {
        const idx =
          e.elementRole === "choice"
            ? (e.choiceIndex ?? 0)
            : parseInt(e.name.replace("Choice ", ""), 10) - 1;
        return [isNaN(idx) ? 0 : idx, e];
      }),
    );

    // Heading + any other element the creator added — rendered generically.
    const otherEls = elements.filter((e) => !isChoiceEl(e) && !isQuestionEl(e));

    const fadeElIds = new Set(
      (slide.animation_data?.interactions || [])
        .filter(Boolean)
        .find((int) => int.event === "slide_start")
        ?.actions?.filter((a) => a.type === "fade")
        ?.map((a) => a.element_id) ?? [],
    );

    const isMultiple = slide.type === "multiple";
    const isPoll = slide.type === "poll";
    const isText = slide.type === "text";
    return (
      <div className="w-full mx-auto absolute inset-0">
        {/* Heading + any other creator-added elements */}
        {otherEls.map((el) => (
          <div
            key={el.id}
            id={`canvas-el-${el.id}`}
            className="absolute"
            style={{
              left: `${el.state?.position?.x ?? 5}%`,
              top: `${el.state?.position?.y ?? 0}%`,
              width: `${el.state?.size?.w ?? 60}%`,
              height: `${el.state?.size?.h ?? 9}%`,
              opacity: fadeElIds.has(el.id) ? 0 : (el.state?.opacity ?? 1),
              transform: `rotate(${el.state?.rotation || 0}deg) scale(${el.state?.scale || 1})`,
            }}
          >
            {el.type === "image" && el.src ? (
              <img
                src={el.src}
                alt=""
                className="w-full h-full object-contain"
              />
            ) : (
              <span
                style={{
                  fontSize: `${el.fontSize || 26}px`,
                  color: el.color,
                  fontWeight: "bold",
                  lineHeight: 1.2,
                }}
              >
                {el.text}
              </span>
            )}
          </div>
        ))}

        {/* Question text */}
        {questionEl && (
          <div
            id={`canvas-el-${questionEl.id}`}
            className="absolute"
            style={{
              left: `${questionEl.state?.position?.x ?? 5}%`,
              top: `${questionEl.state?.position?.y ?? 8}%`,
              width: `${questionEl.state?.size?.w ?? 90}%`,
              height: `${questionEl.state?.size?.h ?? 20}%`,
              opacity: fadeElIds.has(questionEl.id)
                ? 0
                : (questionEl.state?.opacity ?? 1),
              transform: `rotate(${questionEl.state?.rotation || 0}deg)`,
            }}
          >
            <span
              style={{
                fontSize: `${questionEl.fontSize || 34}px`,
                color: questionEl.color,
                fontWeight: "bold",
                lineHeight: 1.2,
              }}
            >
              {slide.question}
            </span>
          </div>
        )}

        {/* Poll / Multiple choices */}

        {(isPoll || isMultiple) &&
          (slide.options || slide.choices || []).map((choice, i) => {
            const el = choiceElMap[i];
            const elId = el?.id;
            const text =
              typeof choice === "object" ? choice.choice_text : choice;
            const isSelected = isMultiple
              ? (answerState?.multipleOptions || []).includes(choice.id)
              : answerState?.pollOption === choice.id;

            return (
              <label
                key={i}
                id={elId ? `canvas-el-${elId}` : undefined}
                className={`absolute flex items-center gap-2 px-1 lg:px-3 py-2 rounded border-2 ${isInteractive ? "cursor-pointer" : ""}`}
                style={{
                  left: `${el?.state?.position?.x ?? 5}%`,
                  top: `${el?.state?.position?.y ?? 34 + i * 15}%`,
                  width: `${el?.state?.size?.w ?? 88}%`,
                  height: `${el?.state?.size?.h ?? 12}%`,
                  opacity:
                    elId && fadeElIds.has(elId) ? 0 : (el?.state?.opacity ?? 1),
                  transform: `rotate(${el?.state?.rotation || 0}deg)`,
                  borderColor: el?.borderColor || "#d1d5db",
                  backgroundColor:
                    el?.backgroundColor || "rgba(255,255,255,0.5)",
                }}
                onClick={
                  isInteractive
                    ? () => {
                        if (!isMultiple && submittedAnswers?.has(currentSlide))
                          return;
                        if (isMultiple) {
                          const current = answerState?.multipleOptions || [];
                          onAnswerChange({
                            multipleOptions: current.includes(choice.id)
                              ? current.filter((id) => id !== choice.id)
                              : [...current, choice.id],
                          });
                        } else {
                          onAnswerChange({ pollOption: choice.id });
                        }
                      }
                    : undefined
                }
              >
                {isInteractive ? (
                  <span
                    className={`flex items-center justify-center shrink-0 w-3 lg:w-5 h-3 lg:h-5 border-2 transition
                      ${isMultiple ? "rounded-md" : "rounded-full"}
                      ${isSelected ? "bg-green-500 border-green-500" : "border-gray-400 bg-white"}`}
                  >
                    {isSelected && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-white text-xs"
                      />
                    )}
                  </span>
                ) : (
                  <span className="shrink-0 w-4 h-4 rounded-full border-2 border-gray-400 bg-white" />
                )}
                <span
                  className={`text-[3vw] lg:text-[1.1vw]`}
                  style={{
                    // fontSize: `${el?.fontSize || 24}px`,
                    color: el?.color,
                  }}
                >
                  {text}
                </span>
              </label>
            );
          })}

        {/* Text answer */}
        {isText && (
          <div
            className="absolute"
            style={{ left: "5%", top: "55%", width: "90%" }}
          >
            {isInteractive ? (
              <textarea
                value={answerState?.text || ""}
                onChange={(e) => onAnswerChange({ text: e.target.value })}
                placeholder="Enter your answer here..."
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-lightPurple"
                rows={3}
              />
            ) : (
              <textarea
                readOnly
                placeholder="Attendees answer here..."
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-400"
                rows={3}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Standard slides (image / blank / non-animated question fallback) ───────
  return (
    <>
      {elements.map((element) => (
        <div
          key={element.id}
          id={`canvas-el-${element.id}`}
          className="absolute"
          style={{
            left: `${Number(element.state?.position?.x || 0)}%`,
            top: `${Number(element.state?.position?.y || 0)}%`,
            width: `${Number(element.state?.size?.w || 20)}%`,
            height: `${Number(element.state?.size?.h || 20)}%`,
            opacity: Number(element.state?.opacity ?? 1),
            transform: `rotate(${Number(element.state?.rotation || 0)}deg) scale(${Number(element.state?.scale || 1)})`,
            // zIndex: 100,
          }}
        >
          {element.type === "text" && (
            <div
              className="w-full h-full flex items-start px-2"
              style={{ containerType: "inline-size" }}
            >
              <span
                style={{
                  fontSize: `${Number(element.fontSize || 28)}px`,
                  color: element.color || "#ffffff",
                  fontWeight: "bold",
                  lineHeight: 1.2,
                  userSelect: "none",
                }}
              >
                {element.text}
              </span>
            </div>
          )}

          {element.type === "image" && element.src && (
            <img
              src={element.src}
              alt=""
              draggable={false}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </div>
      ))}
    </>
  );
};
